<template>
  <div class="dbd">
    <!-- ── Hero ── -->
    <div class="dbd-hero">
      <div class="dbd-hero__bg"/>
      <div class="dbd-hero__content">
        <div class="dbd-hero__left">
          <div class="dbd-hero__icon-wrap"><i class="fas fa-chart-line"/></div>
          <div>
            <h4 class="dbd-hero__title">{{ $t('contractStats.dailyStats') }}</h4>
            <p class="dbd-hero__sub">{{ $t('contractStats.dailyOverview') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Filters ── -->
    <div class="dbd-filter-card">
      <div class="dbd-filter-card__date">
        <calendar-bar v-model="dateRange" @input="refresh" from-key="start" until-key="end"
                      :enable-custom="true"
                      :active-options="['all','today','yesterday','thisWeek','lastWeek','last7Days','thisMonth','lastMonth','last30Days']"/>
      </div>
      <b-row>
        <b-col cols="12" md="4">
          <label class="dbd-filter-label">{{ $t('contractStats.selectBuilding') }}</label>
          <building-select-box v-model="buildings" multiple clearable @input="refresh" size="sm"/>
        </b-col>
        <b-col cols="12" md="8">
          <label class="dbd-filter-label">{{ $t('contractStats.selectProperty') }}</label>
          <real-estate-picker multiple v-model="properties" :buildings="buildings" clearable @input="refresh"/>
        </b-col>
      </b-row>
    </div>

    <!-- Loading -->
    <div v-if="loading.stats" class="dbd-loading">
      <b-spinner variant="primary" style="width:2.5rem;height:2.5rem"/>
    </div>

    <template v-else>
      <!-- ══ Activity cards ══ -->
      <div class="dbd-kpi-grid">
        <div v-for="c in activityCards" :key="c.key" class="dbd-kpi-3d" :style="{'--accent': c.color, '--bg-from': c.bgFrom, '--bg-to': c.bgTo}">
          <div class="dbd-kpi-3d__glow"/>
          <div class="dbd-kpi-3d__icon"><i :class="c.icon"/></div>
          <div class="dbd-kpi-3d__value">{{ c.value }}</div>
          <div class="dbd-kpi-3d__label">{{ c.label }}</div>
          <div v-if="c.platforms && Object.keys(c.platforms).length" class="dbd-kpi-3d__platforms">
            <platform-logo v-for="(cnt, p) in c.platforms" :key="p" :platform="p" size="14px" class="mr-50">
              <span class="dbd-kpi-3d__pcnt">{{ cnt }}</span>
            </platform-logo>
          </div>
        </div>
      </div>

      <!-- ══ Revenue cards ══ -->
      <h6 class="dbd-section"><i class="fa-solid fa-coins mr-50 text-success"/> {{ $t('contractStats.revenue') }}</h6>
      <div class="dbd-rev-grid">
        <div v-for="c in revenueCards" :key="c.key" class="dbd-rev-3d" :style="{'--accent': c.accent}">
          <div class="dbd-rev-3d__stripe"/>
          <div class="dbd-rev-3d__head">
            <span class="dbd-rev-3d__label">{{ c.label }}</span>
            <i :class="c.icon" class="dbd-rev-3d__ic"/>
          </div>
          <div class="dbd-rev-3d__val">{{ c.value | numberFormatter }} <small>€</small></div>
          <div v-if="c.sub" class="dbd-rev-3d__sub"><i class="fa-solid fa-arrow-trend-down mr-25"/> {{ c.sub }}</div>
        </div>
      </div>

      <!-- ══ Quick stats ══ -->
      <div class="dbd-quick">
        <div v-for="s in quickStats" :key="s.label" class="dbd-quick__pill" :style="{'--pill': s.pillColor}">
          <span class="dbd-quick__label">{{ s.label }}</span>
          <span class="dbd-quick__val" :class="s.cls">{{ s.value }}</span>
        </div>
      </div>

      <!-- ══ Charts ══ -->
      <b-row class="mt-50">
        <b-col cols="12" lg="5">
          <div class="dbd-chart-card">
            <div class="dbd-chart-card__head">
              <h6>{{ $t('contractStats.platformDistribution') }}</h6>
              <small class="text-muted">{{ $t('contractStats.calculatedByCheckin') }}</small>
            </div>
            <div class="dbd-chart-card__body">
              <vue-apex-charts v-if="donutSeries.length" ref="donut" type="donut" height="320" :options="donutOpts" :series="donutSeries"/>
              <div v-else class="dbd-no-data">{{ $t('nodatafound') }}</div>
            </div>
          </div>
        </b-col>
        <b-col cols="12" lg="7">
          <div class="dbd-chart-card">
            <div class="dbd-chart-card__head">
              <h6>{{ $t('contractStats.revenueVsCommissions') }}</h6>
              <small class="text-muted">{{ $t('contractStats.calculatedByCheckout') }}</small>
            </div>
            <div class="dbd-chart-card__body">
              <vue-apex-charts v-if="areaSeries.length && areaSeries[0].data.length" ref="area" type="area" height="320" :options="areaOpts" :series="areaSeries"/>
              <div v-else class="dbd-no-data">{{ $t('nodatafound') }}</div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- ══ Reservation table ══ -->
      <div class="dbd-chart-card mt-1">
        <div class="dbd-chart-card__head">
          <h6>{{ $t('contractStats.reservationTable') }}</h6>
        </div>
        <div class="dbd-chart-card__body">
          <contract-rent-stats-table :stats="stats" :loading="loading.stats"/>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {mapActions, mapGetters} from 'vuex'
import CalendarBar from '@/components/widgets/calendar/CalendarBar.vue'
import BuildingSelectBox from '@/components/components/picker/buildingSelectBox.vue'
import RealEstatePicker from '@/components/components/picker/realEstatesPicker.vue'
import PlatformLogo from '@/components/widgets/PlatformLogo.vue'
import ContractRentStatsTable from '@/components/panels/dashboard/contractRentStatsTable.vue'

const emptyStats = () => ({checkin: [], checkout: [], inRange: 0, staying: [], cancelled: []})

export default {
  name: 'contractRentDashboard',
  components: {VueApexCharts, CalendarBar, BuildingSelectBox, RealEstatePicker, PlatformLogo, ContractRentStatsTable},
  data: () => ({dateRange: {}, buildings: [], properties: [], loading: {stats: true}, stats: emptyStats()}),
  created() { this.$root.$on('crm::branch-updated', this.refresh) },
  beforeDestroy() { this.$root.$off('crm::branch-updated', this.refresh) },
  computed: {
    ...mapGetters('appConfig', ['isDarkMode']),
    checkinByPlatforms() { return this.stats.checkin.reduce((a, c) => { const p = c.platform || 'Treasure Home'; a[p] = (a[p] || 0) + 1; return a }, {}) },
    checkoutByPlatforms() { return this.stats.checkout.reduce((a, c) => { const p = c.platform || 'Treasure Home'; a[p] = (a[p] || 0) + 1; return a }, {}) },
    stayingByPlatforms() { return this.stats.staying.reduce((a, c) => { const p = c.platform || 'Treasure Home'; a[p] = (a[p] || 0) + 1; return a }, {}) },
    incomes() {
      const r = {internal: 0, external: 0, total: 0}
      this.stats.checkin.forEach(c => { if (!c.platform || c.platform === 'Treasure Home') r.internal += c.amount; else r.external += c.amount; r.total += c.amount })
      return r
    },
    expenses() {
      const r = {internal: {commission: 0}, external: {commission: 0}, total: {commission: 0}}
      this.stats.checkin.forEach(c => { if (c.isInternalIncome) r.internal.commission += c.commissionAmount || 0; else r.external.commission += c.commissionAmount || 0; r.total.commission += c.commissionAmount || 0 })
      return r
    },
    activityCards() {
      return [
        {key: 'ci', icon: 'fas fa-sign-in-alt', color: '#7367f0', bgFrom: '#7367f0', bgTo: '#9e95f5', value: this.stats.checkin.length, label: this.$t('contractStats.checkins'), platforms: this.checkinByPlatforms},
        {key: 'co', icon: 'fas fa-sign-out-alt', color: '#ff9f43', bgFrom: '#ff9f43', bgTo: '#ffc085', value: this.stats.checkout.length, label: this.$t('contractStats.checkouts'), platforms: this.checkoutByPlatforms},
        {key: 'st', icon: 'fas fa-door-open', color: '#00cfe8', bgFrom: '#00cfe8', bgTo: '#6ee7f7', value: this.stats.staying.length, label: this.$t('contractStats.staying'), platforms: this.stayingByPlatforms},
        {key: 'ca', icon: 'fas fa-times-circle', color: '#ea5455', bgFrom: '#ea5455', bgTo: '#f08182', value: (this.stats.cancelled || []).length, label: this.$t('contractStats.cancellations'), platforms: null},
      ]
    },
    revenueCards() {
      const comm = (v) => `${this.$t('contractStats.commissions')}: ${this.fmt(Math.abs(v))} €`
      return [
        {key: 'int', icon: 'fa-solid fa-filter-circle-dollar', label: this.$t('contractStats.internalIncome'), value: this.incomes.internal, accent: '#28c76f', sub: comm(this.expenses.internal.commission)},
        {key: 'ext', icon: 'fa-solid fa-hand-holding-dollar', label: this.$t('contractStats.externalIncome'), value: this.incomes.external, accent: '#00cfe8', sub: comm(this.expenses.external.commission)},
        {key: 'tot', icon: 'fa-solid fa-landmark', label: this.$t('contractStats.totalIncome'), value: this.incomes.total, accent: '#7367f0', sub: comm(this.expenses.total.commission)},
      ]
    },
    quickStats() {
      const net = this.incomes.total + this.expenses.total.commission
      const avg = this.stats.checkin.length ? (this.incomes.total / this.stats.checkin.length).toFixed(2) : '0.00'
      const total = this.stats.checkin.length + this.stats.checkout.length + this.stats.staying.length
      return [
        {label: this.$t('contractStats.netRevenue'), value: `${this.fmt(net)} €`, cls: net >= 0 ? 'text-success' : 'text-danger', pillColor: '#28c76f'},
        {label: this.$t('contractStats.totalCommissions'), value: `${this.fmt(Math.abs(this.expenses.total.commission))} €`, cls: 'text-danger', pillColor: '#ea5455'},
        {label: this.$t('contractStats.avgPerCheckin'), value: `${avg} €`, cls: '', pillColor: '#7367f0'},
        {label: this.$t('contractStats.totalReservations'), value: total, cls: '', pillColor: '#ff9f43'},
      ]
    },
    /* Donut */
    donutLabels() { return Object.keys(this.checkinByPlatforms) },
    donutSeries() { return Object.values(this.checkinByPlatforms) },
    donutOpts() {
      return {
        chart: {type: 'donut', foreColor: this.isDarkMode ? '#ccc' : '#555', dropShadow: {enabled: true, top: 4, left: 0, blur: 8, opacity: .12}},
        labels: this.donutLabels,
        colors: this.donutLabels.map(p => this.pColor(p)),
        stroke: {width: 3, colors: [this.isDarkMode ? '#283046' : '#fff']},
        legend: {position: 'bottom', fontFamily: 'Montserrat', fontSize: '13px', markers: {width: 10, height: 10, radius: 4}},
        plotOptions: {pie: {donut: {size: '70%', labels: {show: true, name: {fontSize: '14px', fontWeight: 700}, value: {fontSize: '24px', fontWeight: 900, offsetY: 4}, total: {show: true, label: this.$t('contractStats.checkins'), fontSize: '11px'}}}}},
        dataLabels: {enabled: false},
        tooltip: {y: {formatter: v => `${v} ${this.$t('contractStats.reservations')}`}},
      }
    },
    /* Area chart (replaces bar) */
    incomesByPlatformArr() {
      const d = {}
      this.stats.checkin.forEach(c => { const p = c.platform || 'Treasure Home'; if (!d[p]) d[p] = {platform: p, amount: 0, commission: 0}; d[p].amount += c.amount || 0; d[p].commission += c.commissionAmount || 0 })
      return Object.values(d)
    },
    areaOpts() {
      return {
        chart: {type: 'area', foreColor: this.isDarkMode ? '#ccc' : '#555', toolbar: {show: false}, dropShadow: {enabled: true, top: 6, blur: 10, opacity: .08}},
        dataLabels: {enabled: false},
        stroke: {curve: 'smooth', width: 3},
        fill: {type: 'gradient', gradient: {shadeIntensity: 1, opacityFrom: .45, opacityTo: .05, stops: [0, 100]}},
        xaxis: {categories: this.incomesByPlatformArr.map(p => p.platform)},
        yaxis: {labels: {formatter: v => `${v.toFixed(0)} €`}},
        colors: ['#28c76f', '#ea5455'],
        legend: {position: 'top', fontFamily: 'Montserrat', fontSize: '13px'},
        grid: {borderColor: this.isDarkMode ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)', strokeDashArray: 5},
        tooltip: {y: {formatter: v => `${v.toFixed(2)} €`}},
        markers: {size: 5, strokeWidth: 2, hover: {size: 7}},
      }
    },
    areaSeries() {
      return [
        {name: this.$t('contractStats.grossIncome'), data: this.incomesByPlatformArr.map(p => parseFloat(p.amount.toFixed(2)))},
        {name: this.$t('contractStats.commissions'), data: this.incomesByPlatformArr.map(p => parseFloat(Math.abs(p.commission).toFixed(2)))},
      ]
    },
  },
  methods: {
    ...mapActions('realEstateContractsStats', ['getCheckinStats']),
    fmt(v) { return this.$options.filters.numberFormatter(v) },
    pColor(p) { return {'Treasure Home': '#c2aa8e', Airbnb: '#FF4560', Booking: '#3F51B5', Expedia: '#FF9800', Agoda: '#4CAF50', Vrbo: '#96a6b1'}[p] || '#00BCD4' },
    refresh() {
      this.loading.stats = true
      this.getCheckinStats({...this.dateRange, buildings: this.buildings, properties: this.properties})
        .then(data => { this.stats = {...emptyStats(), ...(data?.stats || {})} })
        .finally(() => { this.loading.stats = false })
    },
  },
}
</script>

<style lang="scss" scoped>
/* ── Hero ── */
.dbd-hero { position: relative; border-radius: 18px; overflow: hidden; margin-bottom: 18px; }
.dbd-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); z-index: 0; }
.dbd-hero__content { position: relative; z-index: 1; padding: 22px 28px; }
.dbd-hero__left { display: flex; align-items: center; gap: 16px; }
.dbd-hero__icon-wrap {
  width: 52px; height: 52px; border-radius: 16px; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.2); backdrop-filter: blur(6px); font-size: 1.3rem; color: #fff;
  box-shadow: 0 4px 14px rgba(0,0,0,.15);
}
.dbd-hero__title { font-weight: 800; font-size: 1.2rem; margin: 0; color: #fff; }
.dbd-hero__sub { font-size: .82rem; color: rgba(255,255,255,.75); margin: 0; }

/* ── Filter card ── */
.dbd-filter-card {
  background: #fff; border-radius: 16px; padding: 16px 22px 8px; margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,.05); border: 1px solid rgba(0,0,0,.04);
}
.dbd-filter-card__date { margin-bottom: 8px; }
.dbd-filter-label { font-size: .72rem; font-weight: 700; text-transform: uppercase; color: #64748b; letter-spacing: .04em; margin-bottom: 4px; display: block; }

/* ── Section ── */
.dbd-section { font-size: .78rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #475569; margin: 22px 0 12px 4px; }

/* ── 3D KPI cards ── */
.dbd-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 8px; align-items: stretch; }
@media (max-width: 992px) { .dbd-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) { .dbd-kpi-grid { grid-template-columns: 1fr; } }

.dbd-kpi-3d {
  position: relative; border-radius: 16px; padding: 22px 20px 18px; text-align: center; overflow: hidden;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: linear-gradient(145deg, var(--bg-from), var(--bg-to)); color: #fff;
  box-shadow: 0 8px 28px -4px color-mix(in srgb, var(--accent) 35%, transparent),
              0 2px 6px rgba(0,0,0,.06);
  transition: transform .2s, box-shadow .25s;
}
.dbd-kpi-3d:hover { transform: translateY(-4px) scale(1.015); box-shadow: 0 14px 36px -4px color-mix(in srgb, var(--accent) 45%, transparent), 0 4px 10px rgba(0,0,0,.08); }
.dbd-kpi-3d__glow {
  position: absolute; top: -30px; right: -30px; width: 100px; height: 100px; border-radius: 50%;
  background: rgba(255,255,255,.15); filter: blur(28px); pointer-events: none;
}
.dbd-kpi-3d__icon { font-size: 1.6rem; margin-bottom: 8px; opacity: .85; text-shadow: 0 2px 6px rgba(0,0,0,.15); }
.dbd-kpi-3d__value { font-size: 2.2rem; font-weight: 900; line-height: 1; text-shadow: 0 2px 8px rgba(0,0,0,.12); }
.dbd-kpi-3d__label { font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; opacity: .85; margin-top: 4px; }
.dbd-kpi-3d__platforms { display: flex; justify-content: center; flex-wrap: wrap; gap: 8px; margin-top: auto; padding-top: 10px; border-top: 1px solid rgba(255,255,255,.2); width: 100%; }
.dbd-kpi-3d__pcnt { font-weight: 800; font-size: .8rem; color: #fff; margin-left: 2px; }

/* ── Revenue 3D cards ── */
.dbd-rev-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; align-items: stretch; }
@media (max-width: 992px) { .dbd-rev-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) { .dbd-rev-grid { grid-template-columns: 1fr; } }
.dbd-rev-3d {
  position: relative; background: #fff; border-radius: 16px; padding: 20px 22px; overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 6px 24px rgba(0,0,0,.06), 0 1px 3px rgba(0,0,0,.04);
  transition: transform .2s, box-shadow .25s;
}
.dbd-rev-3d:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,.1); }
.dbd-rev-3d__stripe { position: absolute; left: 0; top: 0; width: 5px; height: 100%; background: var(--accent); border-radius: 5px 0 0 5px; }
.dbd-rev-3d__head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.dbd-rev-3d__label { font-size: .74rem; font-weight: 700; text-transform: uppercase; color: #64748b; letter-spacing: .03em; }
.dbd-rev-3d__ic { font-size: 1.1rem; color: var(--accent); opacity: .6; }
.dbd-rev-3d__val { font-size: 1.8rem; font-weight: 900; color: #1e293b; line-height: 1.15; }
.dbd-rev-3d__val small { font-size: .85rem; opacity: .4; }
.dbd-rev-3d__val { flex: 1; display: flex; align-items: center; }
.dbd-rev-3d__sub { font-size: .74rem; font-weight: 600; color: #ea5455; margin-top: auto; padding-top: 10px; border-top: 1px dashed rgba(0,0,0,.07); }

/* ── Quick stat pills ── */
.dbd-quick { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 18px 0; align-items: stretch; }
@media (max-width: 768px) { .dbd-quick { grid-template-columns: repeat(2, 1fr); } }
.dbd-quick__pill {
  background: #fff; border-radius: 12px; padding: 14px 18px; display: flex; justify-content: space-between; align-items: center;
  box-shadow: 0 3px 14px rgba(0,0,0,.04); border-left: 4px solid var(--pill);
  transition: transform .15s;
}
.dbd-quick__pill:hover { transform: translateX(2px); }
.dbd-quick__label { font-size: .76rem; font-weight: 600; color: #64748b; }
.dbd-quick__val { font-size: .95rem; font-weight: 800; }

/* ── Chart cards ── */
.dbd-chart-card {
  background: #fff; border-radius: 16px; overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,.05); border: 1px solid rgba(0,0,0,.04);
}
.dbd-chart-card__head {
  padding: 16px 22px 12px; border-bottom: 1px solid rgba(0,0,0,.05);
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 4px;
}
.dbd-chart-card__head h6 { font-weight: 800; font-size: .92rem; margin: 0; color: #1e293b; }
.dbd-chart-card__body { padding: 14px 18px; min-height: 340px; display: flex; align-items: center; justify-content: center; }
.dbd-no-data { color: #94a3b8; font-size: .85rem; }

/* ── Loading ── */
.dbd-loading { display: flex; align-items: center; justify-content: center; min-height: 350px; }
</style>

<style lang="scss">
.dark-layout .dbd-hero__bg { background: linear-gradient(135deg, #434190 0%, #6b21a8 100%); }
.dark-layout .dbd-filter-card, .dark-layout .dbd-rev-3d, .dark-layout .dbd-quick__pill, .dark-layout .dbd-chart-card {
  background: #283046 !important; border-color: rgba(255,255,255,.06) !important;
}
.dark-layout .dbd-rev-3d__val, .dark-layout .dbd-chart-card__head h6 { color: #d0d2d6 !important; }
.dark-layout .dbd-rev-3d__label, .dark-layout .dbd-quick__label, .dark-layout .dbd-section, .dark-layout .dbd-filter-label { color: #b4b7bd !important; }
.dark-layout .dbd-rev-3d__sub, .dark-layout .dbd-kpi-3d__platforms { border-color: rgba(255,255,255,.1) !important; }
.dark-layout .dbd-chart-card__head { border-color: rgba(255,255,255,.06) !important; }
</style>
